/**
 * Central Axios Client for Hayat Family
 * Configured with withCredentials for cookie-based authentication
 * Includes global 401 interceptor for session handling
 */
import axios from 'axios';
import { logout } from './auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Create axios instance with global configuration
const axiosClient = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true, // CRITICAL: Always send cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Clear all auth state (cookies, localStorage, sessionStorage)
 * Preserves device UUID for device pairing
 */
const clearAllAuthState = () => {
  console.log('🧹 [Interceptor] Clearing all auth state...');
  
  // Preserve device UUID
  const deviceUUID = localStorage.getItem('hayat_device_uuid');
  
  // Clear storage
  localStorage.clear();
  sessionStorage.clear();
  
  // Restore device UUID
  if (deviceUUID) {
    localStorage.setItem('hayat_device_uuid', deviceUUID);
  }
  
  // Clear cookies by setting them to expired
  document.cookie.split(";").forEach((c) => {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
  
  console.log('✅ [Interceptor] Auth state cleared');
};

/**
 * Response interceptor to handle auth errors globally
 * Handles 401 (Unauthorized) and 404 (User not found) from authenticated endpoints
 */
axiosClient.interceptors.response.use(
  (response) => {
    // Pass through successful responses
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    
    // Determine if this is an authenticated endpoint
    const isAuthenticatedEndpoint = 
      url.includes('/api/family/me') ||
      url.includes('/api/households/') ||
      url.includes('/api/seniors/') ||
      url.includes('/api/reminders/') ||
      url.includes('/api/activity/');
    
    // Handle 401 or 404 on authenticated endpoints
    if (isAuthenticatedEndpoint && (status === 401 || status === 404)) {
      console.warn(`⚠️ Session mismatch detected (${status} on ${url}), refreshing auth state`);
      
      // Clear all auth state
      clearAllAuthState();
      
      // Call logout to clear context state
      try {
        await logout();
      } catch (logoutError) {
        console.error('⚠️ Logout call failed in interceptor:', logoutError);
      }
      
      // Redirect to login page (only if not already there)
      if (typeof window !== 'undefined') {
        if (!window.location.pathname.includes('/login')) {
          console.log('↩️ Redirecting to login page...');
          window.location.href = '/family/login';
        }
      }
    }
    
    // Re-throw error for component-level handling
    return Promise.reject(error);
  }
);

export default axiosClient;
